import type { ActivityDeletionBatchView } from '@internal/spassu-code-contract';

type BatchView = ActivityDeletionBatchView | null;
type ReadBatch = (batchId: string) => Promise<BatchView>;
const INITIAL_DELAY = 3_000;
const MAX_DELAY = 30_000;

/** One serial read loop; pending snapshots do not reset the batch's delay. */
export class DeletionBatchTracker {
  private active = true;
  private batchId: string | null = null;
  private generation = 0;
  private delay = INITIAL_DELAY;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private pending: { generation: number; promise: Promise<void> } | undefined;

  constructor(
    private readonly read: ReadBatch,
    private readonly onView: (view: BatchView) => void,
    private readonly onComplete: () => void,
  ) {}

  resume(batchId: string) {
    if (!this.active) return;
    this.changeBatch(batchId);
    this.poll();
  }

  track(view: BatchView) {
    if (!this.active) return;
    this.changeBatch(view?.pending ? view.batchId : null);
    this.onView(view);
    this.schedule();
  }

  refresh(): Promise<void> {
    if (!this.active || !this.batchId) return Promise.resolve();
    const id = this.batchId;
    if (this.pending) {
      if (this.pending.generation === this.generation)
        return this.pending.promise;
      return this.pending.promise
        .catch(() => undefined)
        .then(() => this.refresh());
    }
    this.clearTimer();
    const generation = this.generation;
    const promise = Promise.resolve()
      .then(() => (this.isCurrent(generation) ? this.read(id) : undefined))
      .then(view => {
        if (!this.isCurrent(generation) || view === undefined) return;
        this.onView(view);
        if (!view?.pending) {
          this.changeBatch(null);
          this.onComplete();
        }
      })
      .finally(() => {
        this.pending = undefined;
        this.schedule();
      });
    this.pending = { generation, promise };
    return promise;
  }

  stop() {
    this.active = false;
    this.changeBatch(null);
  }

  private isCurrent(generation: number) {
    return this.active && generation === this.generation;
  }

  private changeBatch(id: string | null) {
    if (id === this.batchId) return;
    this.clearTimer();
    this.batchId = id;
    this.generation += 1;
    this.delay = INITIAL_DELAY;
  }

  private clearTimer() {
    if (this.timer !== undefined) clearTimeout(this.timer);
    this.timer = undefined;
  }

  private poll() {
    // Automatic reads retry through finally; explicit refreshes expose errors.
    void this.refresh().catch(() => undefined);
  }

  private schedule() {
    if (
      !this.active ||
      !this.batchId ||
      this.pending ||
      this.timer !== undefined
    )
      return;
    this.timer = setTimeout(() => {
      this.timer = undefined;
      this.poll();
    }, this.delay);
    this.delay = Math.min(this.delay * 2, MAX_DELAY);
  }
}
